import { isNft } from "@metaplex-foundation/js";
import type { Connection } from "@solana/web3.js";
import { PublicKey, SystemProgram, TransactionInstruction } from "@solana/web3.js";
import { fromLamports, STEP_FEE_ADDRESS, toLamports } from "@stepfinance/common";
import { Breakpoints } from "@stepfinance/react";

import { STEP_BURN_FEE } from "../../actions/burn/burnToken";
import { ReactComponent as BurnIcon } from "../../assets/flame.svg";
import type { GalleryPaginationConfig } from "../../components/nft/NFTGallery";
import { NFTChangeNotificationDisplay } from "../../components/Notifications/TokenChangeNotificationDisplay";
import type { PageSizes } from "../../components/shared";
import { COLLECTIONS_BY_UPDATE_AUTH } from "../../constants/nft";
import type { BurnNotificationStrings } from "../../contexts/language/languageTypes";
import type { MagicEdenCollectionStats } from "../../models/magiceden/types";
import { getEdition, getMetadata } from "../../models/metaplex";
import type { MetaplexNFT, NFTWithTokenAccount } from "../../models/nfts/nfts";
import type { NotificationContentStates } from "../transactions";
import { formatStringWithPlaceholders } from "../utils";
import { allowForDescriptionOverride, convertNFTNameToCollectionName } from "./string-utils";

/**
 * Algorithm for determining the Collection name of a particular NFT. Even with the metaplex
 * spec, there is no exact science to deriving collection name. Others like Solscan have commented
 * the same thing. Collection name is important for display, but it can also be a way to link
 * collection data when there is no authoritative pubkey available
 */
export const getCollectionName = (nft: MetaplexNFT): string => {
  // Case 0. Display family, if present, as the collection name
  // Case 1. Usually data.collection should be an object with "name" and "family" keys
  // Case 2. In uncommon cases data.collection is set to be the stringy collection name
  // Case 3. In rare cases data.collection is an array
  // Case 4. In rare cases, the collection uses the description field as collection name
  //         We allow overrides for this
  // Case 6. When unspecified, we derive it from the "name" of the NFT itself by chopping
  //         trailing ID numbers if they happen to be there
  const collectionName =
    nft.json?.collection?.family ||
    nft.json?.collection?.name ||
    (Array.isArray(nft.collection) && nft.collection[0].family) ||
    COLLECTIONS_BY_UPDATE_AUTH.get(nft.updateAuthorityAddress.toBase58()) ||
    allowForDescriptionOverride(nft.json?.description) ||
    convertNFTNameToCollectionName(nft.name);

  // Let's ensure that we always return a string from this function in case of
  // a completely unexpected malformed metadata case
  if (typeof collectionName !== "string") {
    return "";
  }

  return collectionName;
};

export const getMECollectionStatByMint = (mint: string, collectionStats: MagicEdenCollectionStats[]) => {
  for (const stat of collectionStats) {
    const set = new Set(stat.mintAddresses);
    if (set.has(mint)) {
      return stat;
    }
  }
};

export const paginationFromWidth = (
  width: number,
  pagination: GalleryPaginationConfig,
  initialPagination: PageSizes[keyof PageSizes],
) => {
  const { pageSizeOptions, pageSize } = pagination;
  let itemsPerRow = 1;
  if (width < Breakpoints.M && width > Breakpoints.S) {
    itemsPerRow = 2;
  }
  if (width < Breakpoints.XXL && width > Breakpoints.M) {
    itemsPerRow = 3;
  }
  if (width > Breakpoints.XXL) {
    itemsPerRow = 4;
  }
  const newPageSizeOptions = initialPagination.map((pageSizeStr) => {
    const pageSize = parseInt(pageSizeStr);
    const newPageSize = pageSize - (pageSize % itemsPerRow);
    return newPageSize.toString();
  });
  const currentPageIndex = pageSizeOptions.indexOf(pageSize.toString());
  const newPageSize = parseInt(newPageSizeOptions[currentPageIndex]);

  return { ...pagination, pageSize: newPageSize, pageSizeOptions: newPageSizeOptions };
};

export const createRedeemableFeeInstruction = async (
  connection: Connection,
  tokenOwner: PublicKey,
  nft: NFTWithTokenAccount,
) => {
  const redeemableBalance = await getRedeemableNFTBalance(connection, nft);
  const burnFee = redeemableBalance * STEP_BURN_FEE;

  const instruction = new TransactionInstruction(
    SystemProgram.transfer({
      fromPubkey: tokenOwner,
      toPubkey: STEP_FEE_ADDRESS,
      lamports: toLamports(burnFee, 9),
    }),
  );
  return instruction;
};

export const getRedeemableNFTBalance = async (connection: Connection, nft: NFTWithTokenAccount, afterFees = false) => {
  let lamports = 0;
  const mintAccount = nft.address;
  const tokenAccount = new PublicKey(nft.associatedTokenAccount.pubkey);
  const isMetaplexRedeemable = !isMetaplexUnableToRedeem(nft);
  if (isMetaplexRedeemable) {
    const editionAccount = getEdition(mintAccount);
    const editionAccountBalance = await connection.getBalance(new PublicKey(editionAccount));
    const metadataAccount = getMetadata(mintAccount);
    const metadataAccountBalance = await connection.getBalance(new PublicKey(metadataAccount));

    lamports += editionAccountBalance + metadataAccountBalance;
  }
  const tokenAccountBalance = await connection.getBalance(tokenAccount);

  lamports += tokenAccountBalance;
  const solAmount = fromLamports(lamports, 9);
  const reclaimBalanceAfterFees = parseFloat((solAmount - solAmount * STEP_BURN_FEE).toFixed(9));
  return afterFees ? reclaimBalanceAfterFees : solAmount;
};

const isMetaplexUnableToRedeem = (nft: MetaplexNFT): boolean => {
  if (isNft(nft)) {
    const isMasterEditionNFT = nft.edition.isOriginal === true;
    if (isMasterEditionNFT) {
      // Master edition can't be burnt if it has editions at his name
      return nft.edition.supply.toNumber() > 0;
    }
  }
  return true;
};

export const getBurnNFTNotifications = (
  name: string,
  amount: number,
  url: string,
  notificationStrings: BurnNotificationStrings,
): NotificationContentStates => {
  return {
    inProgress: {
      message: formatStringWithPlaceholders(notificationStrings.burningTokenName, name),
      description: notificationStrings.transactionInProgress,
    },
    success: {
      icon: <BurnIcon />,
      message: notificationStrings.burnSuccessful,
      description: (
        <>
          <span>
            {formatStringWithPlaceholders(
              notificationStrings.youDidAction.message,
              notificationStrings.youDidAction.burnt,
            )}
          </span>
          <NFTChangeNotificationDisplay amount={amount} name={name} url={url} type={"decrease"} />
        </>
      ),
    },
  };
};
